import React, { useEffect, useMemo, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { TabsWrapper, TabsWrapperHooks } from '@sato/components';

import { trimLeadingHash } from '@utils/link.utils';

import { ITabs, ParsedEntry } from '../types';
import { includesEntry } from '../utils/entry.utils';

import { ResolveEntry } from './ResolveEntry';

export const Tabs = ({ content, title }: ParsedEntry<ITabs>) => {
  const { hash } = useLocation();

  const tabsRef = useRef<TabsWrapperHooks>(null);

  const tabPages = useMemo(
    () =>
      content.map(({ title = '', ...rest }) => ({
        key: rest.id,
        title,
        // exclude title from the grid and use it only for the tab title
        children: (
          <ResolveEntry entry={rest} options={{ inheritLayout: true }} />
        ),
      })),
    [content]
  );

  useEffect(() => {
    if (!hash || !tabsRef.current) return;

    const entryId = trimLeadingHash(hash);
    const tabId = content?.find((tab) => includesEntry([tab], entryId))?.id;

    if (!tabId) return;

    // change tab automatically if a hash is given
    tabsRef.current?.selectTab(tabId);

    /*
     * scroll to the newly focused entry after waiting for a while
     * for the DOM to update and the target element to mount
     */
    const timeout = setTimeout(
      () => window.document.getElementById(entryId)?.scrollIntoView(true),
      500
    );

    return () => {
      clearTimeout(timeout);
    };
  }, [hash, content]);

  if (!content?.length) return null;

  return <TabsWrapper tabPages={tabPages} label={title} ref={tabsRef} />;
};
